import { useAuth0 } from '@auth0/auth0-react';
import { Button, Spinner } from '@cimplico/components';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAcceptInvitation } from '../../queries/use-accept-invitation';
import { Invitation } from '../../types/invitation';
import { formatMixedCase } from '../../utilities/formatting';

type AutoAcceptInvitationProps = {
    id?: string;
};

function AutoAcceptInvitation({ id }: AutoAcceptInvitationProps): JSX.Element {
    const { getAccessTokenSilently } = useAuth0();
    const { mutate, isLoading } = useAcceptInvitation(false);
    const [invitation, setInvitation] = useState<Invitation>();
    const navigate = useNavigate();

    async function navigateToFirm(): Promise<void> {
        if (!invitation) {
            return;
        }

        localStorage.setItem('firm-redirect-to', invitation.firm.shortId);
        await getAccessTokenSilently({ cacheMode: 'off', authorizationParams: { redirect_uri: window.location.origin } });
        window.location.href = `/${invitation.firm.shortId}`;
    }

    useEffect(() => {
        const redirectTo = localStorage.getItem('firm-redirect-to');
        if (redirectTo) {
            localStorage.removeItem('firm-redirect-to');
            navigate(`/${redirectTo}`);
            return;
        }

        if (!id || invitation) {
            return;
        }
        mutate(id, { onSuccess: ({ invitation }) => setInvitation(invitation) });
    }, [id, invitation, mutate, navigate]);

    if (isLoading) {
        return <Spinner />;
    }

    if (!id || !invitation) {
        return (
            <div>
                <div className="flex flex-col items-center">
                    <XCircleIcon className="text-red-600 h-20 w-20" />
                    <p className="text-2xl text-gray-700 mb-2">Invalid Invitation</p>
                    <p className="text-xl text-gray-900 mb-4">
                        This invitation may be deleted or expired. Contact your firm&apos;s administrator for a new invite.
                    </p>
                    <Button label="Go to onboarding" onClick={(): void => navigate('/onboarding')} />
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-col items-center">
                <CheckCircleIcon className="text-green-600 h-20 w-20" />
                <p className="text-2xl text-gray-700 mb-2">Invitation accepted!</p>
                <p className="text-xl text-gray-900 mb-4">
                    You&apos;ve successfully joined {invitation.firm.name} as a {formatMixedCase(invitation.role)}.
                </p>
                <Button label={`Go to ${invitation.firm.name}`} onClick={navigateToFirm} disabled={!invitation} />
            </div>
        </div>
    );
}
export default AutoAcceptInvitation;
